import { useEffect, useMemo, useRef, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import TraceTable from "../TraceTable.jsx";
import { KernelBlock } from "../kernelPrimitives/index.jsx";
import { DEFAULT_LAYER_COLORS } from "../kernelPrimitives/shared.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { query, useQuery } from "../../lib/dataClient.js";
import { bucketFiringByCommand, BUCKETS } from "../../lib/signalStore.js";
import { groupByName } from "../skills/grouping.js";
import { labelForMemory } from "../../lib/memoryNames.js";
import { emptyBodyWindow, pendingBodyIds, unresolvedBodyIds, startFetch, resolveFetch } from "./skillBodyWindow.js";
import MemoryBody from "./MemoryBody.jsx";
import { scopeStepLabel, useLabels } from "../../lib/dimensionDefs.js";

// Trace-list page size — the initial window, and the step each "Load more" adds.
const TRACE_LIMIT = 200;
const normalize = (s) => String(s || "").replace(/\s+/g, " ").trim();

// A group chip both filters the trace list AND opens that copy's content in the
// detail panel. `opensContent` appends the ↗ open-affordance glyph (matching
// FleetSpread's SampleChip) so the per-copy chips read as "opens the body", not
// just a list filter — the plain "All …" reset chip omits it.
function FilterChip({ label, active, onClick, opensContent }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-[3px] rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
        active
          ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
          : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
      ].join(" ")}
    >
      <span>{label}</span>
      {opensContent ? <span aria-hidden className="text-[color:var(--muted-soft)]">↗</span> : null}
    </button>
  );
}

// Per-memory VALUE lens. List-first (same shape as SkillDetailBody): it loads the
// memory's scoped prompts (newest-first, growing by "Load more"), reads each
// in-window prompt's memory body from the backend `memory` signal_tags, and
// groups them. How it groups depends on scope, because a memory file belongs to
// ONE (endpoint, project) — not the fleet:
//
//   • Endpoint in scope (you drilled endpoint → memory): the prompts are one
//     person's file, so distinct bodies are real DRIFT — group by body =
//     versions (v1 → v2 …), the skills-style content-compare.
//   • Top level (no endpoint): the prompts span every endpoint that carries a
//     memory with this slug. Calling cross-person copies "versions" would be a
//     lie, so group by OWNER (endpoint) instead — one row per endpoint showing
//     its current copy, with a "distinct bodies" count for fleet variance.
//
// Either way: clicking a group filters the prompt list AND opens that group's
// content in the right detail panel.
export default function MemoryDetailBody({ nav, slot, ctx }) {
  const scopePath = nav?.scopePath || [];
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const memorySlug = terminal?.dim === "memories" ? (terminal.ids?.[0] ?? terminal.id ?? null) : null;
  // Identities rewrite to an endpoint set, so either pins the prompts to one
  // owner — both count as "endpoint in scope" (→ version/drift grouping).
  const byOwner = !scopePath.some((s) => s.dim === "endpoints" || s.dim === "identities");

  // Identity of the current read — reset the paged list and accumulated bodies
  // whenever the memory / scope / range changes, so a new target never inherits
  // the previous one's loaded rows.
  const queryKey = useMemo(
    () => JSON.stringify([memorySlug, scopePath, ctx?.range ?? null]),
    [memorySlug, scopePath, ctx?.range],
  );

  // The memory's scoped prompts — the trace list, newest-first; the window grows
  // by TRACE_LIMIT each "Load more" (the version/owner picture widens with it,
  // since the groups derive from exactly these rows — loading older prompts is
  // how older versions/owners appear).
  const [rowLimit, setRowLimit] = useState(TRACE_LIMIT);
  const { data: traces = [], loading: tracesLoading } = useQuery(
    "getTraces",
    { scopePath, range: ctx?.range, limit: rowLimit, sort: "newest" },
    { enabled: Boolean(memorySlug) },
  );
  const windowIds = useMemo(() => traces.map((t) => t.id), [traces]);

  // READ each prompt's memory body from the backend `memory` signal_tags
  // (value = JSON {op, body, refs, project}). Paging is INCREMENTAL: each grow of
  // the list fetches bodies only for its NEW event_ids and accumulates them, so a
  // handful of distinct bodies is never re-downloaded per "Load more". Collapse
  // the several rows an event can carry for this slug (e.g. a read AND an edit) to
  // ONE body: prefer a `write`, then a `read` full snapshot; an `edit` carries
  // only the changed hunk so it never stands in as the body.
  const [found, setFound] = useState([]); // accumulated { eventId, body } rows
  const [bodyLoading, setBodyLoading] = useState(false);
  const winRef = useRef(emptyBodyWindow());

  useEffect(() => {
    // Query identity changed — drop the accumulated window and start over.
    winRef.current = emptyBodyWindow();
    setFound([]);
    setRowLimit(TRACE_LIMIT);
  }, [queryKey]);

  useEffect(() => {
    if (!memorySlug) return undefined;
    const missing = pendingBodyIds(windowIds, winRef.current);
    if (!missing.length) return undefined;
    winRef.current = startFetch(winRef.current, missing);
    let alive = true;
    setBodyLoading(true);
    query("getSignalTagValues", { namespace: "memory", tag: memorySlug, scopePath, range: ctx?.range, eventIds: missing })
      .then((res) => {
        if (!alive) return;
        // getSignalTagValues swallows failures to { rows: [], ok: false }; branch
        // on `ok`: release a failed fetch (retry later), graduate a real one (even
        // if it carried no full-content body) so it isn't re-requested.
        const ok = res?.ok !== false;
        winRef.current = resolveFetch(winRef.current, missing, ok);
        if (!ok) return;
        const byEvent = new Map(); // eventId → { op, body } (best full-content op)
        for (const r of res?.rows ?? []) {
          let d = {};
          try { d = JSON.parse(r.value) ?? {}; } catch { continue; }
          if (d.op !== "write" && d.op !== "read") continue; // edit = partial hunk
          const body = String(d.body ?? "");
          if (!body) continue;
          const cur = byEvent.get(r.event_id);
          if (!cur || (d.op === "write" && cur.op !== "write")) byEvent.set(r.event_id, { op: d.op, body });
        }
        const rows = [...byEvent.entries()].map(([eventId, v]) => ({ eventId, body: v.body }));
        if (rows.length) setFound((prev) => [...prev, ...rows]);
      })
      .catch(() => { winRef.current = resolveFetch(winRef.current, missing, false); })
      .finally(() => { if (alive) setBodyLoading(false); });
    return () => {
      alive = false;
      winRef.current = resolveFetch(winRef.current, missing, false);
    };
    // scopePath / range are captured through queryKey; depending on them directly
    // would re-fire on every render (fresh object identities).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowIds, queryKey, memorySlug]);

  // Owner (endpoint id) + timestamp lookups for the loaded rows.
  const ownerOf = useMemo(() => {
    const m = new Map();
    for (const t of traces) m.set(t.id, t.endpointId || "unknown");
    return m;
  }, [traces]);
  const tsOf = useMemo(() => {
    const m = new Map();
    for (const t of traces) m.set(t.id, t.timestamp || 0);
    return m;
  }, [traces]);

  // Owner mode: subscribe to endpoint-label resolution so chips show hostnames
  // as they land from the lazy label cache, not bare ids.
  const ownerIds = useMemo(
    () => [...new Set(found.map((r) => ownerOf.get(r.eventId)).filter(Boolean))],
    [found, ownerOf],
  );
  const labelTick = useLabels(byOwner ? ownerIds.map((id) => ({ dim: "endpoints", id })) : []);

  // Generic groups: { key, label, body, count, ids }. Owner mode keys on the
  // endpoint (label = its hostname, body = that endpoint's most-recent copy);
  // drift mode keys on normalized body (label = v1/v2…).
  const groups = useMemo(() => {
    if (byOwner) {
      return groupByName(found, (r) => ownerOf.get(r.eventId) || "unknown").map((g) => {
        const recent = [...g.rows].sort((a, b) => (tsOf.get(b.eventId) || 0) - (tsOf.get(a.eventId) || 0))[0];
        const epId = ownerOf.get(recent.eventId) || "unknown";
        return {
          key: g.key,
          label: scopeStepLabel({ dim: "endpoints", id: epId }) || epId,
          body: recent.body,
          count: g.rows.length,
          ids: new Set(g.rows.map((r) => r.eventId)),
        };
      });
    }
    return groupByName(found, (r) => normalize(r.body)).map((g, i) => ({
      key: `v${i + 1}`,
      label: `v${i + 1}`,
      body: g.rows[0].body,
      count: g.rows.length,
      ids: new Set(g.rows.map((r) => r.eventId)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [found, byOwner, ownerOf, tsOf, labelTick]);

  // Distinct memory bodies across everything loaded — the fleet-variance number
  // shown in owner mode (where group count is endpoints, not bodies).
  const distinctBodies = useMemo(
    () => new Set(found.map((r) => normalize(r.body))).size,
    [found],
  );

  const [groupFilter, setGroupFilter] = useState(null);
  const selectedGroup = useMemo(
    () => (groupFilter ? groups.find((g) => g.key === groupFilter) : null),
    [groups, groupFilter],
  );
  // Selecting a group filters the ALREADY-LOADED rows — no refetch.
  const rows = useMemo(
    () => (selectedGroup ? traces.filter((t) => selectedGroup.ids.has(t.id)) : traces),
    [traces, selectedGroup],
  );

  // Activity surface: each group as an overlapping line over the loaded time
  // span (group's prompts bucketed like a signal's firings — reuse the same
  // bucketer by shaping the id→group map as a Map<id, [{command}]>).
  const groupSeries = useMemo(() => {
    const asResults = new Map();
    for (const g of groups) {
      for (const id of g.ids) {
        const arr = asResults.get(id) ?? [];
        arr.push({ command: g.key });
        asResults.set(id, arr);
      }
    }
    const byCmd = bucketFiringByCommand(traces, asResults);
    return groups.map((g, i) => ({
      label: g.label,
      series: byCmd.get(g.key) || new Array(BUCKETS).fill(0),
      color: DEFAULT_LAYER_COLORS[i % DEFAULT_LAYER_COLORS.length],
    }));
  }, [traces, groups]);

  const memoryLabel = memorySlug ? labelForMemory(memorySlug) : "";

  // Ancestor scope without the terminal memory step — the prefix a cross-link
  // swaps its target onto (keeping the endpoint/identity drill intact, exactly
  // like ToolDetailBody hopping tool → mcp_server).
  const ancestorScope = scopePath.slice(0, -1);

  function openGroup(g) {
    setGroupFilter(g.key);
    ctx?.onOpenDetail?.({
      eyebrow: memoryLabel,
      title: byOwner
        ? `${g.label} · ${g.count} prompt${g.count === 1 ? "" : "s"}`
        : `Version ${g.key.slice(1)} · ${g.count} prompt${g.count === 1 ? "" : "s"}`,
      // Rich content (not plain `body`): the memory's text with its index
      // pointers / wikilinks resolved into live navigation or ghost markers.
      content: (
        <MemoryBody
          body={g.body}
          slug={memorySlug}
          ancestorScope={ancestorScope}
          range={ctx?.range}
          onNavigate={(targetSlug) =>
            ctx?.onNavigatePath?.([...ancestorScope, { dim: "memories", ids: [targetSlug], id: targetSlug }])
          }
        />
      ),
      detailKey: `${memorySlug}:${g.key}`,
    });
    slot?.onExpandDetail?.();
  }
  const onSelectTrace = (id) => { ctx?.traceLog?.onSelectTrace?.(id); slot?.onExpandDetail?.(); };

  if (!memorySlug) {
    return (
      <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
        <PageHero eyebrow="Memory" title="No memory selected" />
      </ScrollArea>
    );
  }

  // Distinct PROMPTS carrying a captured body (not found.length — one prompt maps
  // to one body here, but keep the intent explicit and robust).
  const bodyCount = new Set(found.map((r) => r.eventId)).size;
  // "reading" covers the gap between a grown list and its bodies arriving — so the
  // status never flashes "no content" mid-page.
  const readingBodies = bodyLoading || unresolvedBodyIds(windowIds, winRef.current).length > 0;
  const status = readingBodies
    ? "reading memory bodies…"
    : bodyCount === 0
      ? "No captured content for this memory in scope yet."
      : byOwner
        ? `${bodyCount} prompt${bodyCount === 1 ? "" : "s"} carry this memory · ${groups.length} endpoint${groups.length === 1 ? "" : "s"} · ${distinctBodies} distinct ${distinctBodies === 1 ? "body" : "bodies"}`
        : `${bodyCount} prompt${bodyCount === 1 ? "" : "s"} carry this memory's content · ${groups.length} version${groups.length === 1 ? "" : "s"}${groups.length > 1 ? " (drift)" : ""}`;

  const metrics = byOwner
    ? [
        { label: "With content", value: bodyCount ? String(bodyCount) : "–", note: "prompts carrying the body" },
        { label: "Endpoints", value: bodyCount ? String(groups.length) : "–" },
        { label: "Distinct bodies", value: bodyCount ? String(distinctBodies) : "–", note: distinctBodies > 1 ? "copies differ across the fleet" : "one copy fleet-wide" },
      ]
    : [
        { label: "With content", value: bodyCount ? String(bodyCount) : "–", note: "prompts carrying the body" },
        { label: "Versions", value: bodyCount ? String(groups.length) : "–", note: groups.length > 1 ? "content differs" : "stable" },
      ];

  // Load-more: the trace list is capped at rowLimit; getTraces reports the true
  // scope total. Growing rowLimit widens the list (and thus the groups). Gate on
  // "did the page fill to the cap" rather than length < total: the deduped row
  // count can sit a hair under count_distinct, which would otherwise leave a
  // "Load N more" that never clears.
  const scopeTotal = traces?.total ?? null;
  const hasMore = traces.length >= rowLimit && (scopeTotal == null || traces.length < scopeTotal);
  const listFooter = hasMore ? (
    <div className="flex justify-center px-[6px] py-[8px]">
      <button
        type="button"
        onClick={() => setRowLimit((l) => l + TRACE_LIMIT)}
        disabled={tracesLoading || bodyLoading}
        className="rounded-[2px] border border-[color:var(--line)] px-[12px] py-[5px] text-[11px] leading-[1.2] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 disabled:cursor-default cursor-pointer"
      >
        {tracesLoading
          ? "Loading…"
          : scopeTotal != null
            ? `Load ${Math.min(TRACE_LIMIT, scopeTotal - traces.length)} more`
            : "Load more"}
        <span className="text-[color:var(--muted-soft)]">
          {scopeTotal != null ? ` · ${traces.length} of ${scopeTotal} loaded` : ` · ${traces.length} loaded`}
        </span>
      </button>
    </div>
  ) : null;

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
      <PageHero
        eyebrow="Memory"
        title={memoryLabel}
        detail={byOwner
          ? "Endpoints carrying this memory. Select an endpoint to read its current copy, or select a row to inspect the prompt."
          : "Prompts that accessed this memory file. Filter by version, then select a version to read it or a row to inspect the prompt."}
        metrics={metrics}
      />
      <span className={NOTE_CLASS}>{status}</span>
      <KernelBlock spec={{ kind: "chart", mode: "line", layers: groupSeries, height: 130, range: ctx?.range, title: byOwner ? "By endpoint" : "Version distribution" }} />
      <TraceTable
        toolbar={
          groups.length ? (
            <div className="flex flex-wrap items-center gap-[4px]">
              <FilterChip label={byOwner ? "All endpoints" : "All versions"} active={!groupFilter} onClick={() => setGroupFilter(null)} />
              {groups.map((g) => (
                <FilterChip key={g.key} label={`${g.label} · ${g.count}`} active={groupFilter === g.key} onClick={() => openGroup(g)} opensContent />
              ))}
            </div>
          ) : null
        }
        rows={rows}
        footer={listFooter}
        selectedTraceId={ctx?.traceLog?.selectedTraceId || ""}
        onSelectTrace={onSelectTrace}
        showEndpoint={ctx?.traceLog?.showEndpoint}
        loading={tracesLoading}
        emptyMessage={bodyLoading ? "Reading…" : "No prompts carry this memory's content in scope."}
      />
    </ScrollArea>
  );
}
